import React, { useMemo, useState, useEffect } from "react";
import { Country, State } from "country-state-city";
import SuggestiveSelect from "../../common/SuggestiveSelect";

export default function Step3StartupInfo({
  formData,
  setFormData,
  // setLogoFile,
  setLogoPreview,
  logoPreview,
  errors,
  isLoading,
  onSubmit,
  validateStartupInfo,
}) {
  // Track field touch status to show validation on blur
  const [touchedFields, setTouchedFields] = useState({});

  // Track logo upload status
  const [logoStatus, setLogoStatus] = useState({
    uploading: false,
    error: "",
    success: false,
  });

  // State options based on selected country
  const [stateOptions, setStateOptions] = useState([]);

  // Get all countries
  const countries = useMemo(() => Country.getAllCountries(), []);

  // Update states when country changes
useEffect(() => {
    if (formData.country) {
      const selectedCountry = countries.find(
        (country) => country.name === formData.country
      );
      if (selectedCountry) {
        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
        setStateOptions(countryStates);
        
        // Clear state if it's not valid for the new country
        if (formData.state && !countryStates.some(state => state.name === formData.state)) {
          setFormData((prev) => ({ ...prev, state: "" }));
        }
      } else {
        setStateOptions([]);
        setFormData((prev) => ({ ...prev, state: "" }));
      }
    } else {
      setStateOptions([]);
      setFormData((prev) => ({ ...prev, state: "" }));
    }
  }, [formData.country, countries, formData.state, setFormData]);


  // Industry options
  const industries = [
    "Technology",
    "Healthcare",
    "Finance",
    "Education",
    "E-commerce",
    "Manufacturing",
    "Transportation",
    "Agriculture",
    "Real Estate",
    "Energy",
    "Biotechnology",
    "Media & Entertainment",
    "Consulting",
    "Renewable Energy",
    "Artificial Intelligence",
    "Cybersecurity",
    "SaaS",
    "Social Impact",
    "Other",
  ];

  const startupStages = [
    "Idea Stage",
    "Prototype",
    "Early Traction",
    "Product-Market Fit",
    "Scaling",
    "Growth",
    "Expansion",
    "Mature",
  ];

    // Format options for SuggestiveSelect component
  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.name,
        label: country.name,
      })),
    [countries]
  );

  const stateSelectOptions = useMemo(
    () =>
      stateOptions.map((state) => ({
        value: state.name,
        label: state.name,
      })),
    [stateOptions]
  );

  const industrySelectOptions = useMemo(
    () =>
      industries.map((industry) => ({
        label: industry,
        value: industry,
      })),
    [industries]
  );

  const stageSelectOptions = useMemo(
    () =>
      startupStages.map((stage) => ({
        label: stage,
        value: stage,
      })),
    [startupStages]
  );


  // Memoize dropdown options for performance
  // const countryOptions = useMemo(
  //   () =>
  //     countries.map((country) => (
  //       <option key={country.isoCode} value={country.name}>
  //         {country.name}
  //       </option>
  //     )),
  //   [countries]
  // );

  // const stateSelectOptions = useMemo(
  //   () =>
  //     stateOptions.map((state) => (
  //       <option key={state.isoCode} value={state.name}>
  //         {state.name}
  //       </option>
  //     )),
  //   [stateOptions]
  // );

  const industryOptions = useMemo(
    () =>
      industries.map((industry) => (
        <option key={industry} value={industry}>
          {industry}
        </option>
      )),
    []
  );

  const stageOptions = useMemo(
    () =>
      startupStages.map((stage) => (
        <option key={stage} value={stage}>
          {stage}
        </option>
      )),
    []
  );

  // Handle input changes
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear specific error when user changes field
    if (errors && errors[name]) {
      setTouchedFields((prev) => ({ ...prev, [name]: true }));
    }
  };

   // Handle SuggestiveSelect changes (country/state)
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Mark field as touched
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    // Clear specific error when user changes field
    if (errors && errors[name]) {
      setTouchedFields((prev) => ({ ...prev, [name]: true }));
    }
  };

  // Mark field as touched on blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  // Handle logo upload with validation
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    setLogoStatus({
      uploading: true,
      error: "",
      success: false,
    });

    // Validate file type and size
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/svg+xml",
    ];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      setLogoStatus({
        uploading: false,
        error: "Invalid file type. Use JPEG, PNG, GIF, or SVG.",
        success: false,
      });
      return;
    }

    if (file.size > maxSize) {
      setLogoStatus({
        uploading: false,
        error: "File size exceeds 2MB.",
        success: false,
      });
      return;
    }

    // setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setLogoStatus({
        uploading: false,
        error: "",
        success: true,
      });
    };
    reader.readAsDataURL(file);
  };

  // Handle logo removal
  const handleRemoveLogo = () => {
    // setLogoFile(null);
    setLogoPreview(null);
    setLogoStatus({
      uploading: false,
      error: "",
      success: false,
    });
    const fileInput = document.getElementById("logo");
    if (fileInput) fileInput.value = "";
  };

  // Validate URL
  const validateWebsite = (e) => {
    const value = e.target.value;
    if (
      value &&
      !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value)
    ) {
      setFormData((prev) => ({
        ...prev,
        errors: { ...prev.errors, website: "Invalid URL format." },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        errors: { ...prev.errors, website: null },
      }));
    }
    handleChange(e);
  };

  // Handle elevator pitch with real-time validation
  const handlePitchChange = (e) => {
    const value = e.target.value;
    handleChange(e);
  };

  // Handle form submission with validation
  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allFields = {
      startupName: true,
      // contactPersonName: true,
      country: true,
      state: true,
      industry: true,
      startupStage: true,
      contactNumber: true,
      elevatorPitch: true,
    };

    setTouchedFields(allFields);

    // Validate form
    if (validateStartupInfo && validateStartupInfo()) {
      onSubmit(e);
    } else {
      // Scroll to the first error
      const firstErrorElement = document.querySelector(".error-msg");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  return (
    <>
      <div className="sec-heading centered">
        <div className="content-area">
          <h2
            className="title wow fadeInUp"
            style={{ fontSize: "1.75rem" }}
            data-wow-delay=".4s">
            Tell Us About Your Startup
          </h2>
          <p className="subtitle-text wow fadeInUp" data-wow-delay=".6s">
            Provide details about your innovative venture
          </p>
        </div>
      </div>

      <div className="contact-form-inputs wow fadeInUp" data-wow-delay=".6s">
        <div
          className="custom-form-area input-boxed"
          style={{ padding: "0px" }}>
          <form className="main-form" onSubmit={handleSubmit}>
            <div className="row">
              {/* Startup Name */}
              <div className="col-12 ">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors && errors.startupName && touchedFields.startupName
                        ? "is-invalid"
                        : ""
                    }`}
                    id="startupName"
                    name="startupName"
                    type="text"
                    value={formData.startupName || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your startup name"
                    aria-label="Startup name"
                    aria-invalid={
                      errors && errors.startupName ? "true" : "false"
                    }
                    style={{
                      borderColor:
                        errors &&
                        errors.startupName &&
                        touchedFields.startupName
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="startupName">
                    Startup Name{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors &&
                    errors.startupName &&
                    touchedFields.startupName && (
                      <span
                        className="error-msg"
                        role="alert"
                        style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                        {errors.startupName}
                      </span>
                    )}
                </div>
              </div>

              {/* Logo Upload */}
              {/* <div className="col-12 mb-4">
                <label className="form-label">
                  Logo{" "}
                  <span className="req" style={{ color: "#dc3545" }}>
                    *
                  </span>
                </label>
                <div className="logo-upload-area">
                  <div className="d-flex align-items-center flex-wrap">
                    {logoPreview && (
                      <div className="logo-preview me-3 mb-2">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "contain",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link text-danger p-0 mt-1"
                          onClick={handleRemoveLogo}
                          aria-label="Remove logo">
                          Remove
                        </button>
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <input
                        type="file"
                        id="logo"
                        name="logo"
                        className={`form-control ${
                          errors && errors.logo ? "is-invalid" : ""
                        }`}
                        onChange={handleLogoUpload}
                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                        aria-label="Upload startup logo"
                        aria-invalid={errors && errors.logo ? "true" : "false"}
                        style={{
                          padding: "10px",
                          border: "1px solid #ced4da",
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        Supported: JPEG, PNG, GIF, SVG. Max 2MB. Recommended:
                        Square logo, 500x500 pixels
                      </small>

                      {logoStatus.error && (
                        <span
                          className="error-msg d-block mt-1"
                          role="alert"
                          style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                          {logoStatus.error}
                        </span>
                      )}

                      {errors && errors.logo && (
                        <span
                          className="error-msg d-block mt-1"
                          role="alert"
                          style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                          {errors.logo}
                        </span>
                      )}

                      {logoStatus.success && (
                        <span
                          className="success-msg d-block mt-1"
                          style={{ color: "#28a745", fontSize: "0.875rem" }}>
                          Logo uploaded successfully!
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Contact Person Name */}
              {/* <div className="col-12 ">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors &&
                      errors.contactPersonName &&
                      touchedFields.contactPersonName
                        ? "is-invalid"
                        : ""
                    }`}
                    id="contactPersonName"
                    name="contactPersonName"
                    type="text"
                    value={formData.contactPersonName || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Name of primary contact"
                    aria-label="Contact person name"
                    aria-invalid={
                      errors && errors.contactPersonName ? "true" : "false"
                    }
                    style={{
                      borderColor:
                        errors &&
                        errors.contactPersonName &&
                        touchedFields.contactPersonName
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="contactPersonName">
                    Contact Person Name{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors &&
                    errors.contactPersonName &&
                    touchedFields.contactPersonName && (
                      <span
                        className="error-msg"
                        role="alert"
                        style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                        {errors.contactPersonName}
                      </span>
                    )}
                </div>
              </div> */}

              {/* Country */}
              <div className="col-md-6 ">
                <div className="input-wrapper">
                  <SuggestiveSelect
                    id="country"
                    name="country"
                    // label="Country"
                    placeholder="Select your country"
                    value={formData.country || ""}
                    onChange={handleSelectChange}
                    options={countryOptions}
                    />
                   <label className="input-label" htmlFor="country">
                    Country{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  {/*<span className="b-border" />
                  {errors && errors.country && touchedFields.country && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.country}
                    </span>
                  )} */}
                </div>
              </div>

              {/* State/Province */}
              <div className="col-md-6 ">
                <div className="input-wrapper">
                  <SuggestiveSelect
                    id="state"
                    name="state"
                    // label="State/Province"
                    placeholder="Select your state/province"
                    value={formData.state || ""}
                    onChange={handleSelectChange}
                    options={stateSelectOptions}
                    disabled={!formData.country} // Disable if no country selected
                  />
                  
                  <label className="input-label" htmlFor="state">
                    State/Province{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  {/* <span className="b-border" />
                  {errors && errors.state && touchedFields.state && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.state}
                    </span>
                  )} */}
                </div>
              </div>

              {/* Industry */}
              {/* <div className="col-md-6 ">
                <div className="input-wrapper">
                  <select
                    className={`text-input ${
                      errors && errors.industry && touchedFields.industry
                        ? "is-invalid"
                        : ""
                    }`}
                    id="industry"
                    name="industry"
                    value={formData.industry || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-label="Industry"
                    aria-invalid={errors && errors.industry ? "true" : "false"}
                    style={{
                      borderColor:
                        errors && errors.industry && touchedFields.industry
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}>
                    <option value="" disabled>
                      Select Industry
                    </option>
                    {industryOptions}
                  </select>
                  <label className="input-label" htmlFor="industry">
                    Industry{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors && errors.industry && touchedFields.industry && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.industry}
                    </span>
                  )}
                </div>
              </div> */}

              {/* Website */}
              <div className="col-md-6 ">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors && errors.website && touchedFields.website
                        ? "is-invalid"
                        : ""
                    }`}
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website || ""}
                    onChange={validateWebsite}
                    onBlur={handleBlur}
                    placeholder="https://www.yourwebsite.com"
                    aria-label="Website URL"
                    aria-invalid={errors && errors.website ? "true" : "false"}
                    style={{
                      borderColor:
                        errors && errors.website && touchedFields.website
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="website">
                    Website
                  </label>
                  <span className="b-border" />
                  {errors && errors.website && touchedFields.website && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.website}
                    </span>
                  )}
                </div>
              </div>

              {/* Startup Stage */}
              {/* <div className="col-md-6 ">
                <div className="input-wrapper">
                  <select
                    className={`text-input ${
                      errors &&
                      errors.startupStage &&
                      touchedFields.startupStage
                        ? "is-invalid"
                        : ""
                    }`}
                    id="startupStage"
                    name="startupStage"
                    value={formData.startupStage || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-label="Startup stage"
                    aria-invalid={
                      errors && errors.startupStage ? "true" : "false"
                    }
                    style={{
                      borderColor:
                        errors &&
                        errors.startupStage &&
                        touchedFields.startupStage
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}>
                    <option value="" disabled>
                      Select Startup Stage
                    </option>
                    {stageOptions}
                  </select>
                  <label className="input-label" htmlFor="startupStage">
                    Startup Stage{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors &&
                    errors.startupStage &&
                    touchedFields.startupStage && (
                      <span
                        className="error-msg"
                        role="alert"
                        style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                        {errors.startupStage}
                      </span>
                    )}
                </div>
              </div> */}

              {/* Contact Number */}
              <div className="col-md-6 ">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors &&
                      errors.contactNumber &&
                      touchedFields.contactNumber
                        ? "is-invalid"
                        : ""
                    }`}
                    id="contactNumber"
                    name="contactNumber"
                    type="number"
                    value={formData.contactNumber || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="+1 (123) 456-7890"
                    aria-label="Contact number"
                    aria-invalid={
                      errors && errors.contactNumber ? "true" : "false"
                    }
                    style={{
                      borderColor:
                        errors &&
                        errors.contactNumber &&
                        touchedFields.contactNumber
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="contactNumber">
                    Contact Number{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors &&
                    errors.contactNumber &&
                    touchedFields.contactNumber && (
                      <span
                        className="error-msg"
                        role="alert"
                        style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                        {errors.contactNumber}
                      </span>
                    )}
                </div>
              </div>

              {/* Elevator Pitch */}
              <div className="col-12 ">
                <div className="input-wrapper">
                  <textarea
                    className={`text-input ${
                      errors &&
                      errors.elevatorPitch &&
                      touchedFields.elevatorPitch
                        ? "is-invalid"
                        : ""
                    }`}
                    id="elevatorPitch"
                    name="elevatorPitch"
                    rows="4"
                    value={formData.elevatorPitch || ""}
                    onChange={handlePitchChange}
                    onBlur={handleBlur}
                    maxLength="200"
                    placeholder="Briefly describe your startup (max 200 characters)"
                    aria-label="Elevator pitch"
                    aria-invalid={
                      errors && errors.elevatorPitch ? "true" : "false"
                    }
                    style={{
                      borderColor:
                        errors &&
                        errors.elevatorPitch &&
                        touchedFields.elevatorPitch
                          ? "#dc3545"
                          : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="elevatorPitch">
                    Elevator Pitch{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  {/* <span className="b-border" /> */}
                  <div className="d-flex justify-content-between">
                    {/* {errors &&
                    errors.elevatorPitch &&
                    touchedFields.elevatorPitch ? (
                      <span
                        className="error-msg"
                        role="alert"
                        style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                        {errors.elevatorPitch}
                      </span>
                    ) : (
                      <span />
                    )} */}
                    {/* <small
                      className={`text-muted ${
                        formData.elevatorPitch &&
                        formData.elevatorPitch.length > 180
                          ? "text-warning"
                          : ""
                      }`}
                      style={{
                        fontWeight:
                          formData.elevatorPitch &&
                          formData.elevatorPitch.length > 180
                            ? "bold"
                            : "normal",
                      }}>
                      {formData.elevatorPitch
                        ? formData.elevatorPitch.length
                        : 0}
                      /200
                    </small> */}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="col-12 submit-wrapper">
                <button
                  type="submit"
                  className="btn-solid w-100"
                  disabled={isLoading}
                  aria-label="Continue to payment"
                  style={{
                    backgroundColor: "#09aff4",
                    color: "#fff",
                    border: "none",
                    padding: "12px",
                    borderRadius: "6px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    opacity: isLoading ? "0.7" : "1",
                  }}>
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    "Continue to Payment"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
